import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock.js";
import { getTextStats } from "../lib/textStats.js";
import { Checkbox } from "./ui/controls";
import { LoadingState, EmptyState, Spinner } from "./ui/states";
import { useToast } from "./ui/Toast";
import { useConfirm } from "./ui/ConfirmDialog";
import { IconX, IconTerminal } from "./ui/icons";

export type LlmLogListItem = {
  id: string;
  createdAt: string;
  action: string;
  actionLabel: string;
  kind: string;
  model: string;
  modelDisplay: string;
  modelProviderLabel: "Cursor" | "Google";
  modelNameLabel: string;
  leadId: string | null;
  espoUserId: string | null;
  espoUserFirstName: string | null;
  espoUserCrmUrl: string | null;
  usageCategory: string | null;
  usageCategoryLabel: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  durationMs: number | null;
};

type Props = {
  setErr: (e: string | null) => void;
};

export type LlmLogModalState = {
  logId: string;
  kind: "request" | "response";
  requestText: string;
  responseText: string;
};

function modalTitle(kind: "request" | "response"): string {
  return kind === "request" ? "Zapytanie do LLM" : "Odpowiedź LLM";
}

function modalBody(state: LlmLogModalState): string {
  return state.kind === "request" ? state.requestText : state.responseText;
}

async function adminFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    credentials: "include",
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);
  return data as T;
}

function formatPlDateOnly(date: string | number | Date): string {
  return new Date(date).toLocaleDateString("pl-PL");
}

function formatPlTimeOnly(date: string | number | Date): string {
  return new Date(date).toLocaleTimeString("pl-PL");
}

function formatDurationSeconds(durationMs: number | null): string {
  if (durationMs == null) return "-";
  const seconds = durationMs / 1000;
  return `${seconds.toLocaleString("pl-PL", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} s`;
}

function formatTokenPair(input: number | null, output: number | null): string {
  const inStr = input === null ? "-" : String(input);
  const outStr = output === null ? "-" : String(output);
  return `${inStr} / ${outStr}`;
}

function IconViewRequest({ className = "" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
      <path d="M10 9H8" />
    </svg>
  );
}

function IconViewResponse({ className = "" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function LlmLogDetailModal({
  modal,
  onClose,
  onSwitchKind,
  onCopyError,
}: {
  modal: LlmLogModalState;
  onClose: () => void;
  onSwitchKind: (kind: "request" | "response") => void;
  onCopyError: (message: string) => void;
}) {
  useBodyScrollLock(true);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [copied, setCopied] = useState(false);

  const body = modalBody(modal);
  const otherKind = modal.kind === "request" ? "response" : "request";

  useEffect(() => {
    setCopied(false);
    bodyRef.current?.scrollTo(0, 0);
  }, [modal.kind]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const { words, chars } = useMemo(() => getTextStats(body), [body]);

  const copyBody = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      onCopyError("Nie udało się skopiować do schowka.");
    }
  };

  const switchKind = () => onSwitchKind(otherKind);

  return createPortal(
    <div className="llm-log-fullscreen-modal" role="dialog" aria-modal="true" aria-labelledby="llm-log-modal-title">
      <button type="button" className="llm-log-fullscreen-modal__backdrop" aria-label="Zamknij" onClick={onClose} />
      <div className="llm-log-fullscreen-modal__panel" onClick={(e) => e.stopPropagation()}>
        <header className="llm-log-fullscreen-modal__header">
          <div className="llm-log-fullscreen-modal__header-start">
            <h2 id="llm-log-modal-title" className="llm-log-fullscreen-modal__title">
              {modalTitle(modal.kind)}
            </h2>
            <button
              type="button"
              className="btn btn-secondary llm-log-fullscreen-modal__btn"
              onClick={switchKind}
            >
              {otherKind === "response" ? "Pokaż odpowiedź" : "Pokaż zapytanie"}
            </button>
          </div>
          <div className="llm-log-fullscreen-modal__header-actions">
            <button
              type="button"
              className="btn btn-secondary llm-log-fullscreen-modal__btn"
              onClick={() => void copyBody()}
              aria-label={modal.kind === "request" ? "Kopiuj zapytanie" : "Kopiuj odpowiedź"}
            >
              {copied ? "Skopiowano" : "Kopiuj"}
            </button>
            <button type="button" className="btn-icon-only" onClick={onClose} aria-label="Zamknij">
              <IconX size={16} />
            </button>
          </div>
        </header>
        <div ref={bodyRef} className="llm-log-fullscreen-modal__body">
          <pre className="llm-log-fullscreen-modal__pre">{body}</pre>
        </div>
        <footer className="llm-log-fullscreen-modal__footer" aria-live="polite">
          <span>Słowa: {words}</span>
          <span>Znaki: {chars}</span>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

export function LlmLogsPanel({ setErr }: Props) {
  const toast = useToast();
  const confirm = useConfirm();
  const [logs, setLogs] = useState<LlmLogListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [includeEmbeddings, setIncludeEmbeddings] = useState(false);
  const [includeHistoricCse, setIncludeHistoricCse] = useState(false);
  const [modal, setModal] = useState<LlmLogModalState | null>(null);
  const [loadingDetailId, setLoadingDetailId] = useState<string | null>(null);
  const [clearing, setClearing] = useState(false);

  const closeModal = useCallback(() => setModal(null), []);

  const switchModalKind = useCallback((kind: "request" | "response") => {
    setModal((prev) => (prev ? { ...prev, kind } : null));
  }, []);

  const onCopyError = useCallback(
    (message: string) => {
      toast.error(message);
    },
    [toast],
  );

  const loadPage = useCallback(
    async (cursor: string | null, append: boolean) => {
      const params = new URLSearchParams();
      params.set("limit", "50");
      if (includeEmbeddings) params.set("includeEmbeddings", "true");
      if (includeHistoricCse) params.set("includeHistoricCse", "true");
      if (cursor) params.set("cursor", cursor);

      const r = await adminFetch<{ logs: LlmLogListItem[]; nextCursor: string | null }>(
        `/admin/api/llm-logs?${params.toString()}`,
      );
      setLogs((prev) => (append ? [...prev, ...r.logs] : r.logs));
      setNextCursor(r.nextCursor);
    },
    [includeEmbeddings, includeHistoricCse],
  );

  useEffect(() => {
    setLoading(true);
    loadPage(null, false)
      .catch((e) => setErr(String((e as Error).message)))
      .finally(() => setLoading(false));
  }, [loadPage, setErr]);

  const onLoadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    setErr(null);
    try {
      await loadPage(nextCursor, true);
    } catch (e) {
      setErr(String((e as Error).message));
    } finally {
      setLoadingMore(false);
    }
  };

  const showDetail = async (id: string, kind: "request" | "response") => {
    setLoadingDetailId(id);
    setErr(null);
    try {
      const detail = await adminFetch<{ requestText: string; responseText: string }>(
        `/admin/api/llm-logs/${encodeURIComponent(id)}`,
      );
      setModal({
        logId: id,
        kind,
        requestText: detail.requestText,
        responseText: detail.responseText,
      });
    } catch (e) {
      setErr(String((e as Error).message));
    } finally {
      setLoadingDetailId(null);
    }
  };

  const onClearLogs = async () => {
    const ok = await confirm({
      title: "Wyczyścić wszystkie logi zapytań LLM?",
      body: "Tej operacji nie można cofnąć. Wyczyści to też statystyki w zakładce Zużycie i koszty.",
      confirmLabel: "Wyczyść",
      danger: true,
    });
    if (!ok) return;
    setClearing(true);
    try {
      await adminFetch<{ deleted: number }>("/admin/api/llm-logs", { method: "DELETE" });
      setModal(null);
      await loadPage(null, false);
      toast.success("Wyczyszczono logi LLM");
    } catch (e) {
      toast.error("Nie udało się wyczyścić logów", String((e as Error).message));
    } finally {
      setClearing(false);
    }
  };

  if (loading) {
    return (
      <div className="panel">
        <LoadingState text="Ładowanie logów..." />
      </div>
    );
  }

  return (
    <div className="panel">
      <h2 style={{ marginTop: 0 }}>Logi zapytań LLM</h2>
      <p className="muted">
        Pełna historia wywołań LLM (Google Gemini i Cursor - generowanie tekstu oraz embeddingi Gemini).
        Domyślnie ukryte są logi embeddingów - włącz checkbox, aby je zobaczyć.
      </p>

      <div className="view-stack" style={{ gap: 12, marginTop: 16 }}>
        <Checkbox checked={includeEmbeddings} onChange={setIncludeEmbeddings}>
          Pokaż logi z embeddingów
        </Checkbox>
        <Checkbox checked={includeHistoricCse} onChange={setIncludeHistoricCse}>
          Pokaż historyczne logi CSE (Custom Search)
        </Checkbox>
      </div>

      <div className="row" style={{ gap: 12, marginTop: 16, flexWrap: "wrap" }}>
        <button
          type="button"
          className="btn btn-secondary btn--small"
          disabled={clearing || logs.length === 0}
          onClick={() => void onClearLogs()}
        >
          {clearing ? "Czyszczenie…" : "Wyczyść logi"}
        </button>
      </div>

      {logs.length === 0 ? (
        <EmptyState
          title="Brak wpisów"
          desc={
            includeEmbeddings || includeHistoricCse
              ? "Nie ma jeszcze żadnych zapytań LLM do wyświetlenia."
              : "Nie ma zapytań LLM (bez embeddingów i historycznego CSE). Włącz filtry powyżej, aby zobaczyć więcej."
          }
          icon={<IconTerminal size={22} />}
        />
      ) : (
        <>
          <div className="admin-table-wrap" style={{ marginTop: 24 }}>
            <table className="admin-table llm-logs-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th className="llm-logs-table__actions-col">
                    <div className="llm-logs-table__stacked llm-logs-table__stacked--actions">
                      <span>ZAP</span>
                      <span>ODP</span>
                    </div>
                  </th>
                  <th>Tokeny (wej./wyj.)</th>
                  <th>Czas</th>
                  <th>Użytkownik</th>
                  <th>Kategoria</th>
                  <th>Model</th>
                  <th>Rodzaj akcji</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className="llm-logs-table__stacked">
                        <span className="llm-logs-table__date">{formatPlDateOnly(row.createdAt)}</span>
                        <span className="llm-logs-table__time muted">{formatPlTimeOnly(row.createdAt)}</span>
                      </div>
                    </td>
                    <td className="llm-logs-table__actions-col">
                      <div className="llm-logs-table__stacked llm-logs-table__stacked--actions">
                        <button
                          type="button"
                          className="btn btn-secondary btn--small llm-logs-table__action-btn"
                          disabled={loadingDetailId === row.id}
                          aria-label="Pokaż zapytanie"
                          title="Pokaż zapytanie"
                          onClick={() => void showDetail(row.id, "request")}
                        >
                          {loadingDetailId === row.id ? (
                            <Spinner size="s" />
                          ) : (
                            <IconViewRequest className="btn-icon" />
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn--small llm-logs-table__action-btn"
                          disabled={loadingDetailId === row.id}
                          aria-label="Pokaż odpowiedź"
                          title="Pokaż odpowiedź"
                          onClick={() => void showDetail(row.id, "response")}
                        >
                          {loadingDetailId === row.id ? (
                            <Spinner size="s" />
                          ) : (
                            <IconViewResponse className="btn-icon" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="llm-logs-table__tokens">
                      {formatTokenPair(row.inputTokens, row.outputTokens)}
                    </td>
                    <td className="llm-logs-table__duration">{formatDurationSeconds(row.durationMs)}</td>
                    <td>
                      {row.espoUserFirstName && row.espoUserCrmUrl ? (
                        <a href={row.espoUserCrmUrl} target="_blank" rel="noopener noreferrer">
                          {row.espoUserFirstName}
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{row.usageCategoryLabel ?? "-"}</td>
                    <td>
                      <div className="llm-logs-table__stacked llm-logs-table__model">
                        <span>{row.modelProviderLabel}</span>
                        <span className="muted">{row.modelNameLabel}</span>
                      </div>
                    </td>
                    <td>
                      <span>{row.actionLabel}</span>
                      {row.leadId ? (
                        <span className="muted" style={{ display: "block", fontSize: "0.8rem" }}>
                          Lead: {row.leadId}
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {nextCursor ? (
            <div style={{ marginTop: 16 }}>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={loadingMore}
                onClick={() => void onLoadMore()}
              >
                {loadingMore ? "Ładowanie…" : "Wczytaj więcej"}
              </button>
            </div>
          ) : null}
        </>
      )}

      {modal ? (
        <LlmLogDetailModal
          modal={modal}
          onClose={closeModal}
          onSwitchKind={switchModalKind}
          onCopyError={onCopyError}
        />
      ) : null}
    </div>
  );
}
